/**
 * BackupsTab Component
 *
 * Database backup and restore functionality for admins.
 * - List all backups with date, size, and optional comment
 * - Create new backups with optional comment
 * - Download backups
 * - Restore backups with password verification
 * - Delete backups
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { backupsApi, type BackupDTO } from '../../../lib/settingsApi';

// Modal for restore confirmation with password
function RestoreConfirmModal({
  backup,
  onClose,
  onConfirm,
  isRestoring,
  error,
}: {
  backup: BackupDTO;
  onClose: () => void;
  onConfirm: (password: string) => void;
  isRestoring: boolean;
  error: string | null;
}) {
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [step, setStep] = useState<'confirm' | 'password'>('confirm');

  const handleFirstConfirm = () => {
    if (confirmText === 'HERSTELLEN') {
      setStep('password');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim()) {
      onConfirm(password);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 'confirm' ? 'Backup Herstellen' : 'Wachtwoord Bevestigen'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isRestoring}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {step === 'confirm' ? (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h3 className="font-medium text-red-900">Let op! Dit is een destructieve actie.</h3>
                    <p className="mt-1 text-sm text-red-700">
                      Alle huidige data wordt overschreven met de backup van{' '}
                      <strong>{new Date(backup.createdAt).toLocaleString('nl-NL')}</strong>.
                      Dit kan niet ongedaan worden gemaakt.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-700 mb-2">
                  Backup details:
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li><strong>Bestand:</strong> {backup.filename}</li>
                  <li><strong>Datum:</strong> {new Date(backup.createdAt).toLocaleString('nl-NL')}</li>
                  <li><strong>Grootte:</strong> {backup.sizeFormatted}</li>
                  {backup.comment && <li><strong>Opmerking:</strong> {backup.comment}</li>}
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Typ <strong>HERSTELLEN</strong> om door te gaan:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                  placeholder="HERSTELLEN"
                />
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <p className="text-sm text-gray-700 mb-4">
                  Voer je wachtwoord in om de restore te bevestigen.
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Wachtwoord
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Voer je wachtwoord in"
                  autoFocus
                  disabled={isRestoring}
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={isRestoring}
          >
            Annuleren
          </button>
          {step === 'confirm' ? (
            <button
              type="button"
              onClick={handleFirstConfirm}
              disabled={confirmText !== 'HERSTELLEN'}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Doorgaan
            </button>
          ) : (
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={!password.trim() || isRestoring}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isRestoring ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Herstellen...
                </>
              ) : (
                'Herstellen'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Modal for creating a new backup
function CreateBackupModal({
  onClose,
  onCreate,
  isCreating,
}: {
  onClose: () => void;
  onCreate: (comment?: string) => void;
  isCreating: boolean;
}) {
  const [comment, setComment] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate(comment.trim() || undefined);
  };

  return (
    <div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nieuwe Backup Maken</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isCreating}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            <p className="text-sm text-gray-600">
              Er wordt een kopie van de huidige database gemaakt. Je kunt optioneel een opmerking toevoegen.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opmerking (optioneel)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Bijv. 'Voor grote update' of 'Wekelijkse backup'"
                maxLength={500}
                disabled={isCreating}
              />
              <p className="mt-1 text-xs text-gray-500">{comment.length}/500 tekens</p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={isCreating}
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isCreating ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Maken...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Backup Maken
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Upload backup modal
function UploadBackupModal({
  onClose,
  onUpload,
  onUploadAndRestore,
  isUploading,
  error,
}: {
  onClose: () => void;
  onUpload: (file: File, comment?: string) => void;
  onUploadAndRestore: (file: File, password: string, comment?: string) => void;
  isUploading: boolean;
  error: string | null;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [mode, setMode] = useState<'upload' | 'restore'>('upload');
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [step, setStep] = useState<'select' | 'confirm' | 'password'>('select');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.db')) {
        alert('Alleen .db bestanden zijn toegestaan');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    if (mode === 'upload') {
      onUpload(file, comment.trim() || undefined);
    } else if (mode === 'restore') {
      if (step === 'select') {
        setStep('confirm');
      } else if (step === 'confirm' && confirmText === 'HERSTELLEN') {
        setStep('password');
      } else if (step === 'password' && password.trim()) {
        onUploadAndRestore(file, password, comment.trim() || undefined);
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 'select' ? 'Backup Uploaden' : step === 'confirm' ? 'Bevestig Herstel' : 'Wachtwoord Invoeren'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isUploading}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {step === 'select' && (
              <>
                {/* File upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Backup bestand (.db)
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                      <div className={`border-2 border-dashed rounded-lg p-4 text-center hover:border-blue-400 transition-colors ${file ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}>
                        {file ? (
                          <div className="text-sm">
                            <p className="font-medium text-green-700">{file.name}</p>
                            <p className="text-green-600">{formatFileSize(file.size)}</p>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-500">
                            <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Klik om bestand te selecteren
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept=".db"
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={isUploading}
                      />
                    </label>
                    {file && (
                      <button
                        type="button"
                        onClick={() => setFile(null)}
                        className="p-2 text-gray-400 hover:text-red-500"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opmerking (optioneel)
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                    placeholder="Bijv. 'Backup van productie'"
                    maxLength={500}
                    disabled={isUploading}
                  />
                </div>

                {/* Mode selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wat wil je doen?
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="mode"
                        value="upload"
                        checked={mode === 'upload'}
                        onChange={() => setMode('upload')}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Alleen opslaan</p>
                        <p className="text-sm text-gray-500">Upload het bestand als backup voor later gebruik</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="mode"
                        value="restore"
                        checked={mode === 'restore'}
                        onChange={() => setMode('restore')}
                        className="h-4 w-4 text-red-600 focus:ring-red-500"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Uploaden en direct herstellen</p>
                        <p className="text-sm text-gray-500">Upload en vervang de huidige database meteen</p>
                      </div>
                    </label>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
              </>
            )}

            {step === 'confirm' && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h3 className="font-medium text-red-900">Let op! Dit is een destructieve actie.</h3>
                      <p className="mt-1 text-sm text-red-700">
                        Alle huidige data wordt overschreven met het geüploade bestand.
                        Dit kan niet ongedaan worden gemaakt.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-700 mb-2">Bestand details:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li><strong>Bestand:</strong> {file?.name}</li>
                    <li><strong>Grootte:</strong> {file ? formatFileSize(file.size) : '-'}</li>
                    {comment && <li><strong>Opmerking:</strong> {comment}</li>}
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Typ <strong>HERSTELLEN</strong> om door te gaan:
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                    placeholder="HERSTELLEN"
                  />
                </div>
              </div>
            )}

            {step === 'password' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-700">
                  Voer je wachtwoord in om de restore te bevestigen.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Wachtwoord
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Voer je wachtwoord in"
                    autoFocus
                    disabled={isUploading}
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={step === 'select' ? onClose : () => setStep(step === 'password' ? 'confirm' : 'select')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={isUploading}
            >
              {step === 'select' ? 'Annuleren' : 'Terug'}
            </button>
            <button
              type="submit"
              disabled={
                !file ||
                isUploading ||
                (step === 'confirm' && confirmText !== 'HERSTELLEN') ||
                (step === 'password' && !password.trim())
              }
              className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                mode === 'restore' && step !== 'select' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {mode === 'restore' ? 'Herstellen...' : 'Uploaden...'}
                </>
              ) : step === 'select' ? (
                mode === 'upload' ? 'Uploaden' : 'Doorgaan'
              ) : step === 'confirm' ? (
                'Doorgaan'
              ) : (
                'Herstellen'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delete confirmation modal
function DeleteConfirmModal({
  backup,
  onClose,
  onConfirm,
  isDeleting,
}: {
  backup: BackupDTO;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-gray-500/75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Backup Verwijderen</h2>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 mb-4">
            Weet je zeker dat je deze backup wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
          </p>
          <div className="bg-gray-50 rounded-lg p-3">
            <ul className="text-sm text-gray-600 space-y-1">
              <li><strong>Bestand:</strong> {backup.filename}</li>
              <li><strong>Datum:</strong> {new Date(backup.createdAt).toLocaleString('nl-NL')}</li>
              <li><strong>Grootte:</strong> {backup.sizeFormatted}</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={isDeleting}
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isDeleting ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verwijderen...
              </>
            ) : (
              'Verwijderen'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function BackupsTab() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [restoreBackup, setRestoreBackup] = useState<BackupDTO | null>(null);
  const [deleteBackup, setDeleteBackup] = useState<BackupDTO | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch backups
  const { data: backups, isLoading, error } = useQuery({
    queryKey: ['backups'],
    queryFn: backupsApi.list,
  });

  // Create backup mutation
  const createMutation = useMutation({
    mutationFn: backupsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setShowCreateModal(false);
      setSuccessMessage('Backup succesvol aangemaakt');
      setTimeout(() => setSuccessMessage(null), 5000);
    },
  });

  // Delete backup mutation
  const deleteMutation = useMutation({
    mutationFn: (filename: string) => backupsApi.delete(filename),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setDeleteBackup(null);
      setSuccessMessage('Backup succesvol verwijderd');
      setTimeout(() => setSuccessMessage(null), 5000);
    },
  });

  // Restore backup mutation
  const restoreMutation = useMutation({
    mutationFn: ({ filename, password }: { filename: string; password: string }) =>
      backupsApi.restore(filename, { password }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setRestoreBackup(null);
      setRestoreError(null);
      setSuccessMessage(`Database succesvol hersteld. Veiligheidsbackup: ${data.preRestoreBackup}`);
      setTimeout(() => setSuccessMessage(null), 10000);
    },
    onError: (err: Error & { code?: string }) => {
      if (err.message?.includes('Onjuist wachtwoord') || err.code === 'INVALID_PASSWORD') {
        setRestoreError('Onjuist wachtwoord. Probeer het opnieuw.');
      } else {
        setRestoreError(err.message || 'Er is een fout opgetreden bij het herstellen.');
      }
    },
  });

  // Upload backup mutation
  const uploadMutation = useMutation({
    mutationFn: ({ file, comment }: { file: File; comment?: string }) =>
      backupsApi.upload(file, comment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setShowUploadModal(false);
      setUploadError(null);
      setSuccessMessage('Backup succesvol geüpload');
      setTimeout(() => setSuccessMessage(null), 5000);
    },
    onError: (err: Error) => {
      setUploadError(err.message || 'Er is een fout opgetreden bij het uploaden.');
    },
  });

  // Upload and restore mutation
  const uploadRestoreMutation = useMutation({
    mutationFn: ({ file, password, comment }: { file: File; password: string; comment?: string }) =>
      backupsApi.uploadAndRestore(file, password, comment),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      setShowUploadModal(false);
      setUploadError(null);
      setSuccessMessage(`Database succesvol hersteld vanuit geüpload bestand. Veiligheidsbackup: ${data.preRestoreBackup}`);
      setTimeout(() => setSuccessMessage(null), 10000);
    },
    onError: (err: Error & { code?: string }) => {
      if (err.message?.includes('Onjuist wachtwoord') || err.code === 'INVALID_PASSWORD') {
        setUploadError('Onjuist wachtwoord. Probeer het opnieuw.');
      } else {
        setUploadError(err.message || 'Er is een fout opgetreden bij het herstellen.');
      }
    },
  });

  // Handle download
  const handleDownload = async (filename: string) => {
    try {
      await backupsApi.download(filename);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // Handle restore confirmation
  const handleRestoreConfirm = (password: string) => {
    if (restoreBackup) {
      restoreMutation.mutate({ filename: restoreBackup.filename, password });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij laden van backups: {(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Database Backups</h2>
          <p className="mt-1 text-sm text-gray-500">
            Maak backups van de database en herstel indien nodig
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              setUploadError(null);
              setShowUploadModal(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload Backup
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuwe Backup
          </button>
        </div>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Backups list */}
      {backups && backups.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Datum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grootte
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opmerking
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Gemaakt door
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {backups.map((backup) => (
                <tr key={backup.filename} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {new Date(backup.createdAt).toLocaleDateString('nl-NL', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(backup.createdAt).toLocaleTimeString('nl-NL', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {backup.sizeFormatted}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {backup.comment || <span className="text-gray-400 italic">Geen opmerking</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {backup.createdBy || <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleDownload(backup.filename)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Download"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => {
                          setRestoreError(null);
                          setRestoreBackup(backup);
                        }}
                        className="text-amber-600 hover:text-amber-800"
                        title="Herstellen"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setDeleteBackup(backup)}
                        className="text-red-600 hover:text-red-800"
                        title="Verwijderen"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
          <h3 className="mt-4 text-sm font-medium text-gray-900">Geen backups</h3>
          <p className="mt-1 text-sm text-gray-500">
            Maak je eerste backup om je data te beschermen.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            Eerste Backup Maken
          </button>
        </div>
      )}

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-700">
            <p className="font-medium text-blue-900">Over database backups</p>
            <ul className="mt-1 list-disc list-inside space-y-1">
              <li>Backups worden lokaal opgeslagen in de data/backups map</li>
              <li>Bij herstel wordt eerst automatisch een veiligheidsbackup gemaakt</li>
              <li>Herstel vereist wachtwoordverificatie voor extra beveiliging</li>
              <li>Maak regelmatig backups, vooral voor belangrijke wijzigingen</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateBackupModal
          onClose={() => setShowCreateModal(false)}
          onCreate={(comment) => createMutation.mutate({ comment })}
          isCreating={createMutation.isPending}
        />
      )}

      {restoreBackup && (
        <RestoreConfirmModal
          backup={restoreBackup}
          onClose={() => {
            setRestoreBackup(null);
            setRestoreError(null);
          }}
          onConfirm={handleRestoreConfirm}
          isRestoring={restoreMutation.isPending}
          error={restoreError}
        />
      )}

      {deleteBackup && (
        <DeleteConfirmModal
          backup={deleteBackup}
          onClose={() => setDeleteBackup(null)}
          onConfirm={() => deleteMutation.mutate(deleteBackup.filename)}
          isDeleting={deleteMutation.isPending}
        />
      )}

      {showUploadModal && (
        <UploadBackupModal
          onClose={() => {
            setShowUploadModal(false);
            setUploadError(null);
          }}
          onUpload={(file, comment) => uploadMutation.mutate({ file, comment })}
          onUploadAndRestore={(file, password, comment) =>
            uploadRestoreMutation.mutate({ file, password, comment })
          }
          isUploading={uploadMutation.isPending || uploadRestoreMutation.isPending}
          error={uploadError}
        />
      )}
    </div>
  );
}
